import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Boxes,
  DatabaseZap,
  BellRing,
  Activity,
  ArrowUpRight,
  ShieldAlert,
  ShieldCheck,
  PlugZap,
  Loader2,
  CircleDot,
  Mail,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SYSTEM_LABELS } from "@/lib/connectionConfig";
import { GettingStartedChecklist } from "@/components/GettingStartedChecklist";
import { SfOrgSwitcher } from "@/components/SfOrgSwitcher";
import { ErrorState } from "@/components/PageState";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function timeAgo(iso) {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

const STATUS_STYLES = {
  healthy: { dot: "text-emerald-500", label: "Healthy", chip: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  near_limit: { dot: "text-amber-500", label: "Near limit", chip: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
  disconnected: { dot: "text-muted-foreground", label: "Disconnected", chip: "bg-muted text-muted-foreground" },
};

const SEVERITY_STYLES = {
  critical: { border: "border-l-red-500", icon: "text-red-500", chip: "bg-red-500/10 text-red-600 dark:text-red-400" },
  warning: { border: "border-l-amber-500", icon: "text-amber-500", chip: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
};

function MetricCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div
      className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 transition-all duration-200 hover:border-primary/40 hover:shadow-sm"
      data-testid={testid}
    >
      <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-display text-3xl font-bold tracking-tight tabular-nums">{value}</p>
      <p className="mt-1 text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export default function DashboardHome() {
  const { user, loadMe } = useAuth();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [digestOn, setDigestOn] = useState(!!user?.weekly_digest_enabled);
  const [digestDay, setDigestDay] = useState(user?.digest_day ?? 0);
  const [digestHour, setDigestHour] = useState(user?.digest_hour ?? 8);
  const [sendingPreview, setSendingPreview] = useState(false);

  const loadOverview = () => {
    setLoading(true);
    setLoadError("");
    api
      .get("/dashboard/overview")
      .then((r) => {
        setData(r.data);
        setLoadError("");
      })
      .catch((e) => {
        setData(null);
        setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load your workspace snapshot.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadOverview();
  }, []);

  useEffect(() => {
    setDigestOn(!!user?.weekly_digest_enabled);
    if (user?.digest_day != null) setDigestDay(user.digest_day);
    if (user?.digest_hour != null) setDigestHour(user.digest_hour);
  }, [user?.weekly_digest_enabled, user?.digest_day, user?.digest_hour]);

  const saveSchedulePref = async (patch) => {
    try {
      await api.patch("/me/notifications", patch);
      loadMe();
    } catch (e) {
      toast.error("Couldn't update the digest schedule");
    }
  };

  const changeDay = (v) => {
    const d = Number(v);
    setDigestDay(d);
    saveSchedulePref({ digest_day: d });
  };
  const changeHour = (v) => {
    const h = Number(v);
    setDigestHour(h);
    saveSchedulePref({ digest_hour: h });
  };

  const toggleDigest = async (v) => {
    setDigestOn(v);
    try {
      await api.patch("/me/notifications", { weekly_digest_enabled: v });
      toast.success(v ? "Weekly digest turned on" : "Weekly digest turned off");
      loadMe();
    } catch (e) {
      setDigestOn(!v);
      toast.error("Couldn't update your preference");
    }
  };

  const sendPreview = async () => {
    setSendingPreview(true);
    try {
      const { data: res } = await api.post("/digest/preview");
      toast.success(res.message || "Digest sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send preview");
    } finally {
      setSendingPreview(false);
    }
  };

  const metrics = data?.metrics || {};
  const systems = data?.systems || [];
  const alerts = data?.alerts || [];

  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem("gx_iq_banner_dismissed") === "1"
  );
  const dismissBanner = () => {
    sessionStorage.setItem("gx_iq_banner_dismissed", "1");
    setBannerDismissed(true);
  };

  return (
    <div data-testid="dashboard-page">
      {/* Pravadh IQ cross-product banner — shown to all users, dismissible per session */}
      {!bannerDismissed && (
        <div
          className="relative mb-6 flex flex-col gap-3 rounded-xl bg-primary px-5 py-3.5 pr-12 text-primary-foreground sm:flex-row sm:items-center sm:justify-between"
          data-testid="iq-top-banner"
        >
          <div className="flex items-center gap-3">
            <Sparkles className="hidden h-5 w-5 shrink-0 sm:block" />
            <p className="text-sm font-medium">
              Pair GovernX with Pravadh IQ to migrate governed data end to end.
            </p>
          </div>
          <a
            href="https://pravadhiq.pravadhlabs.com/?ref=gx"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full bg-primary-foreground px-4 py-1.5 text-sm font-semibold text-primary transition-transform hover:translate-x-0.5 sm:self-auto"
            data-testid="iq-top-banner-cta"
          >
            Explore Pravadh IQ <ArrowUpRight className="h-4 w-4" />
          </a>
          <button
            onClick={dismissBanner}
            aria-label="Dismiss"
            className="absolute right-3 top-3 rounded-md p-1 text-primary-foreground/80 transition-colors hover:bg-primary-foreground/15 hover:text-primary-foreground"
            data-testid="iq-top-banner-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="mb-8">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Overview</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight lg:text-4xl">
          Welcome, {user?.name?.split(" ")[0]}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          A live snapshot of your connected systems, usage and governance alerts.
        </p>
      </div>

      <div className="mb-4">
        <SfOrgSwitcher onChanged={loadOverview} className="w-fit rounded-lg border border-border bg-card px-3 py-2" />
      </div>

      <GettingStartedChecklist />

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : loadError ? (
        <ErrorState
          title="Overview is unavailable"
          description={loadError}
          onRetry={loadOverview}
          testid="overview-error"
        />
      ) : (
        <>
          {/* Metric cards */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="overview-metrics">
            <MetricCard
              icon={Boxes}
              label="Connected systems"
              value={metrics.connected_systems ?? 0}
              accent="bg-primary/10 text-primary"
              testid="metric-connected-systems"
            />
            <MetricCard
              icon={DatabaseZap}
              label="Records extracted today"
              value={(metrics.records_today ?? 0).toLocaleString()}
              accent="bg-sky-500/10 text-sky-600 dark:text-sky-400"
              testid="metric-records-today"
            />
            <MetricCard
              icon={BellRing}
              label="Active alerts"
              value={metrics.active_alerts ?? 0}
              accent="bg-amber-500/10 text-amber-600 dark:text-amber-400"
              testid="metric-active-alerts"
            />
            <MetricCard
              icon={Activity}
              label="Jobs running"
              value={metrics.jobs_running ?? 0}
              accent="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              testid="metric-jobs-running"
            />
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            {/* Connected systems */}
            <div className="rounded-2xl border border-border bg-card" data-testid="overview-systems">
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <h2 className="font-display text-lg font-bold">Connected systems</h2>
                <Link
                  to="/dashboard/connections"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                  data-testid="overview-view-connections"
                >
                  Manage <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              {systems.length === 0 ? (
                <div className="flex flex-col items-center justify-center px-5 py-14 text-center">
                  <PlugZap className="h-9 w-9 text-muted-foreground" />
                  <p className="mt-3 font-display font-bold">No systems connected</p>
                  <p className="mt-1 text-sm text-muted-foreground">Connect a system to start monitoring it here.</p>
                  <Link
                    to="/dashboard/connections"
                    className="mt-4 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    Connect a system
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {systems.map((s) => {
                    const st = STATUS_STYLES[s.status] || STATUS_STYLES.healthy;
                    return (
                      <Link
                        to={`/dashboard/connections/${s.system}`}
                        key={s.system}
                        className="flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-muted/40"
                        data-testid={`overview-system-${s.system}`}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <CircleDot className={`h-4 w-4 shrink-0 ${st.dot}`} />
                          <div className="min-w-0">
                            <p className="truncate font-medium">{SYSTEM_LABELS[s.system] || s.name}</p>
                            <p className="truncate text-xs text-muted-foreground">{s.detail}</p>
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${st.chip}`}>
                            {st.label}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {s.status === "disconnected" ? "—" : `synced ${timeAgo(s.last_sync_at)}`}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Recent alerts */}
            <div className="rounded-2xl border border-border bg-card" data-testid="overview-alerts">
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <h2 className="font-display text-lg font-bold">Recent alerts</h2>
                <Link
                  to="/dashboard/governance"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                  data-testid="overview-view-governance"
                >
                  Rules <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              {alerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center px-5 py-14 text-center">
                  <ShieldCheck className="h-9 w-9 text-emerald-500" />
                  <p className="mt-3 font-display font-bold">All clear</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    No rule breaches right now. Add a threshold if you want to be notified before a limit is hit.
                  </p>
                  <Link
                    to="/dashboard/governance"
                    className="mt-4 text-sm font-semibold text-primary hover:underline"
                  >
                    Create a rule
                  </Link>
                </div>
              ) : (
                <div className="max-h-[420px] space-y-2.5 overflow-y-auto p-4">
                  {alerts.map((a) => {
                    const sv = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.warning;
                    return (
                      <div
                        key={a.id}
                        className={`rounded-lg border border-border border-l-4 bg-background p-3 ${sv.border}`}
                        data-testid={`overview-alert-${a.id}`}
                      >
                        <div className="flex items-start gap-2.5">
                          <ShieldAlert className={`mt-0.5 h-4 w-4 shrink-0 ${sv.icon}`} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="truncate text-sm font-semibold">{a.system_name}</p>
                              <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${sv.chip}`}>
                                {a.severity}
                              </span>
                            </div>
                            <p className="mt-0.5 text-sm text-muted-foreground">{a.message}</p>
                            <p className="mt-1 text-[11px] text-muted-foreground/70">{timeAgo(a.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Weekly digest */}
          <div
            className="mt-6 rounded-2xl border border-border bg-card p-5"
            data-testid="digest-card"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-display font-bold">Weekly email digest</p>
                  <p className="text-sm text-muted-foreground">
                    {digestOn ? (
                      <>
                        Sent every <span className="font-medium text-foreground">{DAYS[digestDay]}</span> at{" "}
                        <span className="font-medium text-foreground">
                          {String(digestHour).padStart(2, "0")}:00 UTC
                        </span>{" "}
                        to <span className="font-medium text-foreground">{user?.email}</span>.
                      </>
                    ) : (
                      <>Get a weekly summary of usage, alerts and breached governance rules by email.</>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 self-end sm:self-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={sendPreview}
                  disabled={sendingPreview}
                  className="gap-2"
                  data-testid="digest-preview-btn"
                >
                  {sendingPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  Send preview
                </Button>
                <div className="flex items-center gap-2">
                  <Switch checked={digestOn} onCheckedChange={toggleDigest} data-testid="digest-toggle" />
                  <span className="text-sm text-muted-foreground">{digestOn ? "On" : "Off"}</span>
                </div>
              </div>
            </div>

            {digestOn && (
              <div className="mt-4 flex flex-col gap-4 border-t border-border pt-4 sm:flex-row sm:items-center" data-testid="digest-schedule">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Send on</span>
                  <Select value={String(digestDay)} onValueChange={changeDay}>
                    <SelectTrigger className="h-9 w-[140px]" data-testid="digest-day-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS.map((d, i) => (
                        <SelectItem key={d} value={String(i)} data-testid={`digest-day-${i}`}>
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">at</span>
                  <Select value={String(digestHour)} onValueChange={changeHour}>
                    <SelectTrigger className="h-9 w-[120px]" data-testid="digest-hour-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {Array.from({ length: 24 }, (_, h) => (
                        <SelectItem key={h} value={String(h)} data-testid={`digest-hour-${h}`}>
                          {String(h).padStart(2, "0")}:00 UTC
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
